import { CurrencyPipe, NgClass } from '@angular/common';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  booleanAttribute, Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, inject,
  Input, numberAttribute, OnInit, ViewChild, ViewEncapsulation
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { dom as fontAwesomeDom } from '@fortawesome/fontawesome-svg-core';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { APPOINTMENT_CONSTANT } from '../constants/appointment-constants';
import { EVENT_CONSTANT } from '../constants/event-constants';
import { TEMPLATE_CONSTANT } from '../constants/template.constant';
import { TRANSLATION_TEMPLATES } from '../constants/translation-templates-constants';
import { CustomerDbModel } from '../db-models/appointment-custom-fields-dao';
import { AppointmentServiceDao, MappedAppointmentServicesDao } from '../db-models/appointment-service-dao';
import { CategoryDbModel } from '../db-models/categories-db.model';
import { CONFERENCE_ERROR_CODES, CONFERENCE_ERROR_STATE, ConferenceDbModel } from '../db-models/conference-db.model';
import { StoreDbModel } from '../db-models/stores-dao';
import { TimezoneDbModel } from '../db-models/timezone.model';
import { DynamicCombiPrepareResponse } from '../db-models/tour-dao';
import { AnalyticsDao, CustomBookingMessageTemplate, PartnerDao, WidgetBookingInfoDao, WorkerDao } from '../db-models/widget-conf-dao';
import { GlobalObjects, Partner } from '../models/global';
import { IPartnerInformationBody, TokenType } from '../models/partners';
import { ConferenceState, UserState } from '../models/state.model';
import { WidgetColorConf } from '../models/widget-color.model';
import { CssOverwrites, CustomerPrefillDataModel, WidgetConf } from '../models/widget-conf';
import { AdyenResumeHost, AdyenResumeReturn, AdyenReturnParams } from '../models/adyen-resume.model';
import { AdyenResumeService } from '../services/adyen-resume.service';
import { adyenResumeBookingType } from '../services/adyen-resume.util';
import { ADYEN_RESUME_CONSTANTS } from '../constants/adyen-resume.constants';
import { AppointmentService } from '../services/appointment.service';
import { ConferenceService } from '../services/conference.service';
import { CustomEventService } from '../services/custom-event.service';
import { rewriteCustomCssForShadowRoot } from '../services/custom-css.util';
import { CustomersService } from '../services/customers.service';
import { DateUtilsService } from '../services/date-utils.service';
import { GoogleAnalyticsService } from '../services/google-analytics.service';
import { HelperService } from '../services/helper.service';
import { LocalStorageService } from '../services/local-storage.service';
import { LoggerService } from '../services/logger.service';
import { PartnerService } from '../services/partner.service';
import { workerServesStore } from '../services/store-scope.util';
import { TimezoneService } from '../services/timezone.service';
import { UtilService } from '../services/util.service';
import { WidgetUtilService } from '../services/widget-util.service';
import { AlertComponent } from '../shared/components/alert/alert.component';
import { ButtonComponent } from '../shared/components/button/button.component';
import { FinalpageComponent } from '../shared/components/finalpage/finalpage.component';
import { GtmOperationComponent } from '../shared/components/gtm-operation/gtm-operation.component';
import { LoaderComponent } from '../shared/components/loader/loader.component';
import { NewAlertComponent } from '../shared/components/new-alert/new-alert.component';
import { CalioMeetingTemplatePipe } from '../shared/pipes/calio-meeting-template.pipe';
import { TranslationPipe } from '../shared/pipes/translation.pipe';
import { OTP_RESTRICTED_COMPONENTS, WIDGET_APPOINTMENT_TYPE, WIDGET_CONSTANTS, WIDGET_ERROR_CODES, WIDGET_PROTECTION_TYPE, WIDGET_RESTRICTION_PARAMETERS } from './../constants/widget-constants';
import { PasswordProtectionComponent } from "./common/password-protection/password-protection.component";
import { icons } from './fortAwesomeIcons';
import { AppointmentComponent } from './pages/appointment/appointment.component';
import { CompactWidgetComponent } from './pages/compact-widget/compact-widget.component';
import { EventComponent } from './pages/event/event.component';
import { PreDefinedSlotsComponent } from './pages/pre-defined-slots/pre-defined-slots.component';
import { TourComponent } from './pages/tour/tour.component';

const modules = [NgSelectModule, FormsModule, TranslateModule];
const components = [AlertComponent, NewAlertComponent, ButtonComponent, EventComponent, FinalpageComponent, CompactWidgetComponent, GtmOperationComponent, AppointmentComponent, PasswordProtectionComponent, LoaderComponent, PreDefinedSlotsComponent, TourComponent];
const pipes = [CalioMeetingTemplatePipe, TranslationPipe];
/**
 * The booking widget renders inside an open SHADOW ROOT (`ViewEncapsulation.ShadowDom`) so that
 * the host page's stylesheets can never match anything inside the widget and the widget's CSS never
 * reaches the host page. What makes that work (see `widget.component.scss`):
 * - Angular's ShadowDomRenderer registers the shadow root with `SharedStylesHost`, so every child
 *   component's (Emulated) styles are mirrored into it automatically; the GLOBAL stylesheets
 *   (`angular.json` → `styles[]`) are NOT — they are bundled into this component's styles instead;
 * - `:root { --var }` blocks are declared as `:root, :host` in the widget SCSS; partner custom CSS is
 *   adopted by the shadow root in `applyCustomCss()`;
 * - CSS that third-party libraries inject into `document.head` at RUNTIME (FontAwesome svg-core) and
 *   relative `url()` assets of the bundled stylesheets (intl-tel-input flag sprite) are handled in
 *   `injectRuntimeCssIntoShadowRoot()`.
 */
@Component({
  selector: 'app-widget',
  templateUrl: './widget.component.html',
  styleUrls: ['./widget.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  imports: [NgClass, ...modules, ...components, ...pipes],
  providers: [CurrencyPipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WidgetComponent implements OnInit {

  private partnerService = inject(PartnerService);
  private translateService = inject(TranslateService);
  private widgetUtilService = inject(WidgetUtilService);
  private helperService = inject(HelperService);
  private localStorageService = inject(LocalStorageService);
  private utilService = inject(UtilService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private conferenceService = inject(ConferenceService);
  private appointmentService = inject(AppointmentService);
  private customEventService = inject(CustomEventService);
  private destroyRef = inject(DestroyRef);
  protected library = inject(FaIconLibrary);
  private timezoneService = inject(TimezoneService);
  private customersService = inject(CustomersService);
  private adyenResumeService = inject(AdyenResumeService);
  private dateUtilsService = inject(DateUtilsService);
  private hostElementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input('partner') bookingName: string = WIDGET_CONSTANTS.BOOKING_NAME;
  @Input('type') bookingType = WIDGET_CONSTANTS.ANY;
  @Input('lang') set lang(value: string) {
    if (value) {
      if (this.enableLangSetter) {
        this.setLanguage(value, (this.partnerGlobalData || null))
      } else {
        this._lang = value;
      }
    }
  }
  @Input('language') set language(value: string) {
    if (value) {
      if (this.enableLangSetter) {
        this.setLanguage(value, (this.partnerGlobalData || null))
      } else {
        this._language = value;
      }
    }
  };
  @Input('worker-uuid') workerUuid: string;
  @Input('meeting-type-id') meeting_type_id: string;
  @Input('customer-notification-preference') customer_notification_preference: string;
  @Input('booker-worker-id') bookerWorkerId: number;
  @Input('event-id') eventId: string;
  @Input('is-whitelabel') isWhiteLabeled: string;
  @Input('selected-store-id') selectedStoreId: number;
  @Input('selected-store-zip') selectedStoreZipCode: number;
  @Input('selected-store-name') selectedStoreName: string;
  @Input('selected-appointment-service-ids') selectedAppointmentServiceIds: string | string[];
  @Input('selected-appointment-service-category-ids') selectedAppointmentServiceCategoryIds: string | string[];
  @Input({
    alias: 'selected-worker-id',
    transform: numberAttribute
  }) selectedWorkerId: number;
  @Input('selected-worker-email') selectedWorkerEmail: string;
  // Multi-worker preselection pool ("5,7" or number[]). A single id collapses to the scalar
  // selectedWorkerId path; an explicit single-worker identifier always takes precedence.
  @Input('selected-worker-ids') selectedWorkerIds: string | number[];
  @Input('selected-custom-fields') selectedCustomFields: string;
  @Input('selected-customer') selectedCustomerDetail: string;
  @Input('q') base64String: string;
  @Input() internal: string;
  @Input('channel') channel = 'booking_widget';
  @Input('date') calendarPreselectedDate: string;
  @Input('date-context') dateContext: string;
  @Input('date-context-start') dateContextStart: string;
  @Input('date-context-end') dateContextEnd: string;
  @Input('utm-source') utmSource: string;
  @Input('utm-medium') utmMedium: string;
  @Input('utm-campaign') utmCampaign: string;
  @Input('utm-content') utmContent: string;
  @Input('utm-term') utmTerm: string;
  @Input('utm-id') utmId: string;
  @Input('disable-localstorage') disableLocalStorage: number;
  @Input('ui-mode') uiMode: string;
  @Input('hide-grid-switcher') hideGridSwitcher: string = 'false';
  @Input() template = '';
  @Input('internal-comment') internalComment = '';
  @Input('lang-switcher') langSwitcher: any = true;
  @Input('is-preview') isPreview: any = false;
  @Input() token = '';
  @Input('filter-stores') filterStores: string | string[];
  @Input('filter-categories') filterCategories: string | string[];
  @Input('conference-uuid') conferenceUuid: string;
  @Input() debug: string;
  @Input('hide-store') hideStoreSelection = false;
  @Input({ transform: booleanAttribute, alias: 'skip-qualification-question' }) skipQualificationQuestion = false;
  @Input('show-secret-events') showSecretEvents: string;
  @Input('customer-uuid') customerUuid: string;
  @Input('coupon') coupon: string;
  @Input('customer-token') customerToken: string;
  @Input('customer-profile') customerProfileUuid: string;
  @Input('selected-broker') selectedBrokerDetail: string;

  readonly widgetConstants = WIDGET_CONSTANTS;
  readonly baseUrl = environment.baseUrl;
  readonly maintenanceImagePath = environment.maintenanceImagePath;
  protected readonly templateConstant = TEMPLATE_CONSTANT;
  protected readonly templateContent = TRANSLATION_TEMPLATES;

  @ViewChild('calioSection', { static: true }) calioSectionRef: ElementRef;

  isFrame: boolean;
  tokenError = undefined;
  tokenErrorMessage = '';
  selectedJsonCustomFields: { [key: number]: { type: string, value: any } | any };
  selectedJsonCustomerDetail: CustomerPrefillDataModel;
  selectedJsonBrokerDetail: CustomerPrefillDataModel;
  widgetConf: WidgetConf;
  bookTypesEnabled: string[] = [];
  currentBookingType: string;
  enteredAppointmentFromTour = false;
  prefetchedTourCombiService: AppointmentServiceDao | null = null;
  tourLastSelectedUuids: string[] = [];
  partnerGlobalData: Partner;
  globals: GlobalObjects;
  analytics: AnalyticsDao;
  environment: any;
  showDevAlert: boolean;
  isInternal: boolean;
  whiteLabelBoolean: boolean;
  widgetBookingInfo: WidgetBookingInfoDao;
  widgetColorConf: WidgetColorConf;
  showWorkersNotAvailableError = false;
  decodedBase64String: string;
  isBookingDisabled = false;
  isLoggingEnabled = false;
  showSubscriptionError = false;
  subscriptionType: number;
  foundedWorker: WorkerDao;
  cssOverwrites: string;
  eventCategories: CategoryDbModel[] = [];
  eventStores: StoreDbModel[] = [];
  isCompactMode = false;
  localStorageAccessible = true;
  conferenceNotBelongsWarning = false;
  errorInConference: string;
  conferenceState: Partial<ConferenceState> = {};
  workerUuids: string | string[];
  enableLangSetter = false;
  _lang: string;
  _language: string;
  protected isWidgetPasswordProtected = false;
  protected isWidgetRestrictionValidationFailed = false;
  protected isCustomerAuthenticationEnabled = false;
  protected isCustomerAuthenticationInProgress = false;
  protected isCustomerAuthenticationFailed = false;
  protected bookingWidgetMasterSettingsEnabled = true; // This will be used to disable the whole widget (Toggle from Partner management)
  protected isPartnerBlocked = false; // Partner is blocked or deleted — the bootstrap call is refused with 403 partner_blocked
  protected hasBootstrapError = false; // Bootstrap failed for an unrecognized reason (unknown 403 code, 5xx, network) — show "something went wrong, try again later" instead of a stuck skeleton
  protected appointmentExternalBookingEnabled = true;
  protected appointmentInternalBookingEnabled = true;
  protected eventExternalBookingEnabled = true;
  protected pdsExternalBookingEnabled = true;
  protected tourExternalBookingEnabled = true;
  protected customer: CustomerDbModel;
  protected widgetTemplates = Array<CustomBookingMessageTemplate>();
  protected nothingBookableTemplate: CustomBookingMessageTemplate | null = null;
  protected widgetDisabledTemplate: CustomBookingMessageTemplate | null = null;
  /**
   * The partner's wording for a switched-off widget, keyed by locale, as carried in the 403
   * body. Only the refusal path fills this; the success path resolves `widgetDisabledTemplate`.
   *
   * Every locale arrives rather than one, because cakephp cannot tell which language the
   * widget is in - ApiController::initialize() forces the request locale to the brand default
   * before the refusal is raised. The view picks by `_lang`, as the translation pipe does on
   * the success path, so a later language change is honoured too.
   */
  protected widgetDisabledWordings: Record<string, string> = Object.create(null);
  /**
   * The language the embed actually asked for, captured before `setLanguage()` can discard it.
   *
   * On a refused bootstrap there is no partner, so `getDefaultLanguage()` builds its supported
   * list from the brand default and drops any request it does not contain - a `language=en_US`
   * embed ends up on de_CH. The visitor still asked for English, so the wording is looked up
   * under what they asked for before falling back to what the widget settled on.
   */
  protected requestedLang = '';
  protected configurationErrorTemplate: CustomBookingMessageTemplate | null = null;
  protected authenticationFailedTemplate: CustomBookingMessageTemplate | null = null;
  protected subscriptionErrorTemplate: CustomBookingMessageTemplate | null = null;
  protected tourUnavailableTemplate: CustomBookingMessageTemplate | null = null;
  protected timezones = new Array<TimezoneDbModel>();
  protected redirectedPaymentIntent = '';
  protected redirectedPaymentIntentClientSecret = '';
  protected appointmentStripeRedirectionData: Record<string, any>;
  protected eventStripeRedirectionData: Record<string, any>;
  protected pdsStripeRedirectionData: Record<string, any>;
  /** What an Adyen payment return put on the page URL, captured (and removed) on load. */
  private adyenReturnParams: AdyenReturnParams | null = null;
  /**
   * An Adyen payment this tab parked before leaving the page, and that has now come back. Handed to
   * the one host that was paying, which finishes the payment and books exactly what was paid for.
   */
  protected adyenResume: AdyenResumeReturn | undefined;
  /** The hosted checkout returned into a tab that did not start it: show where it is finished. */
  protected adyenReturnedElsewhere = false;
  /** Why a returning Adyen payment could not be resumed here (a translation key), shown above the flow. */
  protected adyenResumeNoticeKey: string | undefined;

  constructor() {
    this.library.addIcons(...icons);
    this.translateService.onLangChange.subscribe(language => this._lang = language.lang);
  }

  private setLanguage(language: string, partner: Partner | PartnerDao): void {
    const finalLang = this.helperService.getDefaultLanguage(partner, language);
    this._lang = finalLang;
    this._language = finalLang;
    this.translateService.use(finalLang);
    this.globals?.user_browser_language && (this.globals.user_browser_language = finalLang);
    this.globals?.user_selected_language && (this.globals.user_selected_language = finalLang);
    this.localStorageService.setItem(WIDGET_CONSTANTS.USER_LANG, finalLang, () => void (0));
  }

  ngOnInit(): void {
    this.injectRuntimeCssIntoShadowRoot();
    this.observeHostSizeForIframeResizer();
    this.langSwitcher = (['false', false].includes(this.langSwitcher) ? false : true);
    this._lang = this._language ? this._language : this._lang;

    if (this.selectedWorkerEmail) {
      this.selectedWorkerEmail = this.selectedWorkerEmail.toLowerCase();
    }

    if (this.selectedAppointmentServiceIds) {
      this.selectedAppointmentServiceIds = (<string>this.selectedAppointmentServiceIds).split(',');
    }

    if (this.selectedAppointmentServiceCategoryIds) {
      this.selectedAppointmentServiceCategoryIds = (<string>this.selectedAppointmentServiceCategoryIds).split(',');
    }

    if (this.filterStores) {
      this.filterStores = (<string>this.filterStores).split(',');
    }

    if (this.filterCategories) {
      this.filterCategories = (<string>this.filterCategories).split(',');
    }

    if (this.selectedCustomFields && this.selectedCustomFields !== '') {
      try {
        this.selectedJsonCustomFields = JSON.parse(this.selectedCustomFields);
      } catch (error) {
        LoggerService.error('[Widget] Invalid JSON format for custom fields:', error);
      }
    }

    if (this.selectedCustomerDetail && this.selectedCustomerDetail !== '') {
      try {
        this.selectedJsonCustomerDetail = JSON.parse(this.selectedCustomerDetail);
        if (this.selectedJsonCustomerDetail?.email) {
          this.selectedJsonCustomerDetail.email = this.helperService.fixEmailSpaceIssue(this.selectedJsonCustomerDetail.email);
        }
      } catch (error) {
        LoggerService.error('[Widget] Invalid JSON format for customer detail:', error);
      }
    }

    if (this.selectedBrokerDetail && this.selectedBrokerDetail !== '') {
      try {
        this.selectedJsonBrokerDetail = JSON.parse(this.selectedBrokerDetail);
        if (this.selectedJsonBrokerDetail?.email) {
          this.selectedJsonBrokerDetail.email = this.helperService.fixEmailSpaceIssue(this.selectedJsonBrokerDetail.email);
        }
      } catch (error) {
        LoggerService.error('[Widget] Invalid JSON format for broker detail:', error);
      }
    }

    this.conferenceUuid && (this.conferenceUuid = this.conferenceUuid.trim());

    this.customerUuid && (this.customerUuid = this.customerUuid.trim());

    this.customerToken && (this.customerToken = this.customerToken.trim());

    this.customerProfileUuid && (this.customerProfileUuid = this.customerProfileUuid.trim());

    const urlQueryStringValue = decodeURIComponent(window.location.search.substring(1));
    const urlParams = urlQueryStringValue.split('&').reduce(function (q, query) {
      const chunks = query.split('=');
      const key = chunks[0];
      let value: string;
      if (chunks[1] === 'NULL' || chunks[1] === 'null' || chunks[1] === 'undefined' || chunks[1] === 'UNDEFINED') {
        value = undefined;
      } else {
        value = chunks[1];
      }
      return (q[key] = value, q);
    }, {});

    // Initialize parent URL detection for cross-origin iframe support
    this.widgetUtilService.initParentUrl();

    // First check URL params (backward compatibility for direct access / same-origin)
    this.redirectedPaymentIntent = this.helperService.getQueryParamValues(urlParams, 'payment_intent', 'payment_intent');
    this.redirectedPaymentIntentClientSecret = this.helperService.getQueryParamValues(urlParams, 'payment_intent_client_secret', 'payment_intent_client_secret');

    // Subscribe to receive params from parent page (cross-origin iframe case)
    // This handles the timing issue where postMessage arrives after ngOnInit runs
    if (!this.redirectedPaymentIntent) {
      this.widgetUtilService.stripeRedirectParamsReceived$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(params => {
          if (params.payment_intent && !this.redirectedPaymentIntent) {
            this.redirectedPaymentIntent = params.payment_intent;
            this.redirectedPaymentIntentClientSecret = params.payment_intent_client_secret;
            LoggerService.log('[Stripe Redirect] Received params from parent via Subject');
          }
        });
    }

    // Before anything else reads the page URL: an Adyen return (redirectResult, adyen_hosted) is
    // captured once and removed, so it can neither be replayed by Back / a reload nor travel into
    // the next payment's return_url. The record it belongs to is matched once the partner is known.
    this.adyenReturnParams = this.adyenResumeService.captureReturnParams();

    const url = new URL(window.location.href);
    url.searchParams.delete("payment_intent");
    url.searchParams.delete("payment_intent_client_secret");
    url.searchParams.delete("redirect_status");
    window.history.replaceState({}, "", url);

    if (urlParams['q']) {
      this.base64String = urlParams['q'];
    }

    let base64Params: any;
    if (this.base64String && this.base64String !== 'null' && this.base64String !== null) {
      this.decodedBase64String = decodeURIComponent(window.atob(decodeURIComponent(this.base64String)));
      base64Params = this.decodedBase64String.split('&').reduce(function (q, query) {
        const chunks = query.split('=');
        const key = chunks[0];
        let value: string;
        if (chunks[1] === 'NULL' || chunks[1] === 'null' || chunks[1] === 'undefined' || chunks[1] === 'UNDEFINED') {
          value = undefined;
        } else {
          value = chunks[1];
        }
        return (q[key] = value, q);
      }, {});
    }

    this.isFrame = Boolean(urlParams['isFrame']);
    this.isCompactMode = (this.widgetConstants.COMPACT === this.bookingType);

    if (this.isFrame) {
      this.bookingName = urlParams['partner'];
      this.bookingType = urlParams['type'];
      this.isCompactMode = (this.widgetConstants.COMPACT === this.bookingType);
      this._lang = urlParams['language'] ? urlParams['language'] : urlParams['lang'] ? urlParams['lang'] : this._lang;
      this.template = urlParams['template'];
      this.token = urlParams['token'];
      this.calendarPreselectedDate = urlParams['date'];
      this.langSwitcher = ['false', false].includes(this.helperService.getQueryParamValues(urlParams, 'lang-switcher', 'lang_switcher')) ? false : true;
      urlParams['debug'] === "true" && (this.debug = urlParams['debug']);
      this.isPreview = this.helperService.getQueryParamValues(urlParams, 'is-preview', 'is_preview') ? true : false;
      this.isLoggingEnabled = this.helperService.getQueryParamValues(urlParams, 'enable_logs', 'enable-logs') ? true : false;
      this.isBookingDisabled = this.helperService.getQueryParamValues(urlParams, 'disable_bookings', 'disable-bookings') ? true : false;
      this.disableLocalStorage = this.helperService.getQueryParamValues(urlParams, 'disable_localstorage', 'disable-localstorage') ? 1 : 0;

      this.isLoggingEnabled && (environment.production = false);

      // Date context starts
      this.dateContext = this.helperService.getQueryParamValues(urlParams, 'date_context', 'date-context');
      // Date context ends

      // Date context start date starts
      this.dateContextStart = this.helperService.getQueryParamValues(urlParams, 'date_context_start', 'date-context-start');
      // Date context start date ends

      // Date context end date starts
      this.dateContextEnd = this.helperService.getQueryParamValues(urlParams, 'date_context_end', 'date-context-end');
      // Date context end date ends

      // worker uuid starts
      this.workerUuid = this.helperService.getQueryParamValues(urlParams, 'worker-uuid', 'worker_uuid');
      // worker uuid ends

      // meeting types starts
      this.meeting_type_id = this.helperService.getQueryParamValues(urlParams, 'meeting-type-id', 'meeting_type_id');
      // meeting types ends

      // customer notification preference starts
      this.customer_notification_preference = this.helperService.getQueryParamValues(urlParams, 'customer-notification-preference', 'customer_notification_preference');
      // customer notification preference ends

      // utm source starts
      this.utmSource = this.helperService.getQueryParamValues(urlParams, 'utm_source', 'utm-source');
      // utm source ends

      // utm medium starts
      this.utmMedium = this.helperService.getQueryParamValues(urlParams, 'utm_medium', 'utm-medium');
      // utm medium ends

      // utm content starts
      this.utmContent = this.helperService.getQueryParamValues(urlParams, 'utm_content', 'utm-content');
      // utm content ends

      // utm campaign starts
      this.utmCampaign = this.helperService.getQueryParamValues(urlParams, 'utm_campaign', 'utm-campaign');
      // utm campaign ends

      // utm term starts
      this.utmTerm = this.helperService.getQueryParamValues(urlParams, 'utm_term', 'utm-term');
      // utm term ends

      // utm id starts
      this.utmId = this.helperService.getQueryParamValues(urlParams, 'utm_id', 'utm-id');
      // utm id ends

      // booker worker uuid starts
      this.bookerWorkerId = this.helperService.getQueryParamValues(urlParams, 'booker-worker-id', 'booker_worker_id');
      // booker worker uuid ends

      // whitelabel starts
      this.isWhiteLabeled = this.helperService.getQueryParamValues(urlParams, 'is-whitelabel', 'is_whitelabel');
      // whitelabel ends

      // internal starts
      this.internal = urlParams['internal'];
      // internal ends

      // RR-v2 channel — iframe mode carries it on the query string starts
      this.channel = this.helperService.getQueryParamValues(urlParams, 'channel', 'channel') || this.channel;
      // RR-v2 channel ends

      // event id starts
      this.eventId = this.helperService.getQueryParamValues(urlParams, 'event-id', 'event_id');
      // event id ends

      // define UI Mode for events listing starts
      this.uiMode = this.helperService.getQueryParamValues(urlParams, 'ui-mode', 'ui_mode');
      // define UI Mode for events listing ends

      // hide switcher to change events listing layuot starts
      this.hideGridSwitcher = this.helperService.getQueryParamValues(urlParams, 'hide-grid-switcher', 'hide_grid_switcher');
      // hide switcher to change events listing layuot ends

      // hide store selection starts
      this.hideStoreSelection = this.helperService.getQueryParamValues(urlParams, 'hide-store', 'hide_store') === 'true' ? true : false;
      // hide store selection ends

      // skip qualification question starts
      this.skipQualificationQuestion = this.helperService.getQueryParamValues(urlParams, 'skip-qualification-question', 'skip_qualification_question') === 'true' ? true : false;
      // skip qualification question ends

      // show secret events starts
      this.showSecretEvents = this.helperService.getQueryParamValues(urlParams, 'show-secret-events', 'show_secret_events') || this.showSecretEvents;
      // show secret events ends

      // coupon starts
      this.coupon = this.helperService.getQueryParamValues(urlParams, 'coupon', 'coupon');
      // coupon ends

      // store id starts
      this.selectedStoreId = this.helperService.getQueryParamValues(urlParams, 'store_id', 'store-id');
      // store id ends

      // store zip code starts
      this.selectedStoreZipCode = this.helperService.getQueryParamValues(urlParams, 'store-zip', 'store_zip');
      // store zip code ends

      // store name starts
      this.selectedStoreName = this.helperService.getQueryParamValues(urlParams, 'store_name', 'store-name');
      if (this.selectedStoreName) {
        this.selectedStoreName = decodeURIComponent(this.selectedStoreName);
      }
      // store name ends

      // worker id starts
      this.selectedWorkerId = this.helperService.getQueryParamValues(urlParams, 'worker_id', 'worker-id');
      this.selectedWorkerId = this.selectedWorkerId !== undefined && !isNaN(Number(this.selectedWorkerId)) ? Number(this.selectedWorkerId) : undefined;
      // worker id ends

      // worker ids (subset) starts — []-suffixed key is the array-param convention
      // (like filter-stores[]); the plain key stays accepted as an alias for hand-written embeds.
      const rawSelectedWorkerIds = this.helperService.getQueryParamValues(urlParams, 'worker_ids[]', 'worker-ids[]')
        ?? this.helperService.getQueryParamValues(urlParams, 'worker_ids', 'worker-ids');
      if (rawSelectedWorkerIds !== undefined) {
        this.selectedWorkerIds = rawSelectedWorkerIds;
      }
      // worker ids (subset) ends

      // worker email starts
      this.selectedWorkerEmail = this.helperService.getQueryParamValues(urlParams, 'worker_email', 'worker-email');
      this.selectedWorkerEmail = this.selectedWorkerEmail ? this.selectedWorkerEmail.toLowerCase() : undefined;
      // worker email ends

      // internal comment
      this.internalComment = this.helperService.getQueryParamValues(urlParams, 'internal_comment', 'internal-comment');
      this.internalComment = this.internalComment ? decodeURIComponent(this.internalComment) : undefined;
      // internal comment

      // Appointment services start
      const appointmentServiceIds: any[] = this.helperService.getQueryStringKey('service', urlQueryStringValue, true);
      this.selectedAppointmentServiceIds = appointmentServiceIds ? appointmentServiceIds : [];
      if (this.selectedAppointmentServiceIds.length > 0) {
        this.selectedAppointmentServiceIds = this.selectedAppointmentServiceIds[0].split(',');
      }
      // Appointment services ends

      // Appointment category start
      const appointmentCategoryIds: any[] = this.helperService.getQueryStringKey('category', urlQueryStringValue, true);
      this.selectedAppointmentServiceCategoryIds = appointmentCategoryIds ? appointmentCategoryIds : [];
      if (this.selectedAppointmentServiceCategoryIds.length > 0) {
        this.selectedAppointmentServiceCategoryIds = this.selectedAppointmentServiceCategoryIds[0].split(',');
      }
      // Appointment category ends

      // filter stores start
      const filterStoresIds = this.helperService.getQueryParamValues(urlParams, 'filter-stores[]', 'filter_stores[]') as string;
      if (filterStoresIds) {
        this.filterStores = filterStoresIds.split(',');
      }
      // filter stores end

      // filter categories start
      const filterCategoriesIds = this.helperService.getQueryParamValues(urlParams, 'filter-categories[]', 'filter_categories[]') as string;
      if (filterCategoriesIds) {
        this.filterCategories = filterCategoriesIds.split(',');
      }
      // filter categories ends

      // conference uuid start
      const conferenceUuid = this.helperService.getQueryParamValues(urlParams, 'conference-uuid', 'conference_uuid') as string;
      conferenceUuid && (this.conferenceUuid = conferenceUuid.trim());
      // conference uuid ends

      // Customer starts
      const tempCustomerData: CustomerPrefillDataModel = this.helperService.getQueryStringKey('customer', urlQueryStringValue, false);
      this.selectedJsonCustomerDetail = tempCustomerData ? tempCustomerData : undefined;
      if (this.selectedJsonCustomerDetail?.email) {
        this.selectedJsonCustomerDetail.email = this.helperService.fixEmailSpaceIssue(this.selectedJsonCustomerDetail.email);
      }
      // Customer ends

      // Broker starts
      const tempBrokerData: CustomerPrefillDataModel = this.helperService.getQueryStringKey('broker', urlQueryStringValue, false);
      this.selectedJsonBrokerDetail = tempBrokerData ? tempBrokerData : undefined;
      if (this.selectedJsonBrokerDetail?.email) {
        this.selectedJsonBrokerDetail.email = this.helperService.fixEmailSpaceIssue(this.selectedJsonBrokerDetail.email);
      }
      // Broker ends

      // customer uuid starts
      this.customerUuid = this.helperService.getQueryParamValues(urlParams, 'customer-uuid', 'customer_uuid');
      // customer uuid ends

      // customer token starts
      this.customerToken = this.helperService.getQueryParamValues(urlParams, 'customer-token', 'customer_token');
      // customer token ends

      // customer profile starts
      this.customerProfileUuid = this.helperService.getQueryParamValues(urlParams, 'customer-profile', 'customer_profile');
      // customer profile ends

      // Custom field starts
      const temp1: { [key: number]: { type: string, value: any } | any } = this.helperService
        .getQueryStringKey('custom_field', urlQueryStringValue, false);
      const temp2: { [key: number]: { type: string, value: any } | any } = this.helperService
        .getQueryStringKey('custom-field', urlQueryStringValue, false);
      if (temp1) {
        this.selectedJsonCustomFields = temp1;
      } else if (temp2) {
        this.selectedJsonCustomFields = temp2;
      } else {
        this.selectedJsonCustomFields = undefined;
      }
      // Custom field ends
    }

    if (base64Params) {
      this.bookingName = base64Params['partner'] ? base64Params['partner'] : this.bookingName;
      this.bookingType = base64Params['type'] ? base64Params['type'] : this.bookingType;
      base64Params['debug'] === "true" && (this.debug = base64Params['debug']);
      this.isCompactMode = (this.widgetConstants.COMPACT === this.bookingType);
      this._lang = base64Params['language'] ? base64Params['language'] : base64Params['lang'] ? base64Params['lang'] : this._lang;
      this.template = base64Params['template'] ? base64Params['template'] : this.template;
      this.token = base64Params['token'] ? base64Params['token'] : this.token;
      this.calendarPreselectedDate = base64Params['date'] ? base64Params['date'] : this.calendarPreselectedDate;
      this.langSwitcher = ['false', false].includes(this.helperService.getQueryParamValues(urlParams, 'lang-switcher', 'lang_switcher')) ? false : this.langSwitcher;
      this.isPreview = this.helperService.getQueryParamValues(base64Params, 'is-preview', 'is_preview') ? true : this.isPreview;

      this.uiMode = this.helperService.getQueryParamValues(base64Params, 'ui-mode', 'ui_mode');
      this.hideGridSwitcher = this.helperService.getQueryParamValues(base64Params, 'hide-grid-switcher', 'hide_grid_switcher');
      this.hideStoreSelection = this.helperService.getQueryParamValues(base64Params, 'hide-store', 'hide_store') === 'true' ? true : false;
      this.skipQualificationQuestion = this.helperService.getQueryParamValues(base64Params, 'skip-qualification-question', 'skip_qualification_question') === 'true' ? true : false;
      this.coupon = this.helperService.getQueryParamValues(base64Params, 'coupon', 'coupon');
      this.isLoggingEnabled = this.helperService.getQueryParamValues(base64Params, 'enable_logs', 'enable-logs') ? true : this.isLoggingEnabled;
      this.isBookingDisabled = this.helperService.getQueryParamValues(base64Params, 'disable_bookings', 'disable-bookings') ? true : this.isBookingDisabled;
      this.disableLocalStorage = this.helperService.getQueryParamValues(base64Params, 'disable_localstorage', 'disable-localstorage') ? 1 : this.disableLocalStorage;

      // Date context starts
      const dateContext: string = this.helperService.getQueryParamValues(base64Params, 'date_context', 'date-context');
      this.dateContext = dateContext ? dateContext : this.dateContext;
      // Date context ends

      // Date context start date starts
      const dateContextStart: string = this.helperService.getQueryParamValues(base64Params, 'date_context_start', 'date-context-start');
      this.dateContextStart = dateContextStart ? dateContextStart : this.dateContextStart;
      // Date context start date ends

      // Date context end date starts
      const dateContextEnd: string = this.helperService.getQueryParamValues(base64Params, 'date_context_end', 'date-context-end');
      this.dateContextEnd = dateContextEnd ? dateContextEnd : this.dateContextEnd;
      // Date context end date ends

      // worker uuid starts
      const workerUuid: string = this.helperService.getQueryParamValues(base64Params, 'worker-uuid', 'worker_uuid');
      this.workerUuid = workerUuid ? workerUuid : this.workerUuid;
      // worker uuid ends

      // meeting type starts
      const meeting_type_id: string = this.helperService.getQueryParamValues(base64Params, 'meeting-type-id', 'meeting_type_id');
      this.meeting_type_id = meeting_type_id ? meeting_type_id : this.meeting_type_id;
      // meeting type ends

      // customer notification preference starts
      const customer_notification_preference: string = this.helperService.getQueryParamValues(base64Params, 'customer-notification-preference', 'customer_notification_preference');
      this.customer_notification_preference
        = customer_notification_preference ? customer_notification_preference : this.customer_notification_preference;
      // customer notification preference ends

      // utm source starts
      const utmSource: string = this.helperService.getQueryParamValues(base64Params, 'utm_source', 'utm-source');
      this.utmSource = utmSource ? utmSource : this.utmSource;
      // utm source ends

      // utm medium starts
      const utmMedium: string = this.helperService.getQueryParamValues(base64Params, 'utm_medium', 'utm-medium');
      this.utmMedium = utmMedium ? utmMedium : this.utmMedium;
      // utm medium ends

      // utm content starts
      const utmContent: string = this.helperService.getQueryParamValues(base64Params, 'utm_content', 'utm-content');
      this.utmContent = utmContent ? utmContent : this.utmContent;
      // utm content ends

      // utm campaign starts
      const utmCampaign: string = this.helperService.getQueryParamValues(base64Params, 'utm_campaign', 'utm-campaign');
      this.utmCampaign = utmCampaign ? utmCampaign : this.utmCampaign;
      // utm campaign starts

      // utm term starts
      const utmTerm: string = this.helperService.getQueryParamValues(base64Params, 'utm_term', 'utm-term');
      this.utmTerm = utmTerm ? utmTerm : this.utmTerm;
      // utm term ends

      // utm id starts
      const utmId: string = this.helperService.getQueryParamValues(base64Params, 'utm_id', 'utm-id');
      this.utmId = utmId ? utmId : this.utmId;
      // utm id ends

      // booker worker uuid starts
      const bookerWorkerId: number = this.helperService.getQueryParamValues(base64Params, 'booker-worker-id', 'booker_worker_id');
      this.bookerWorkerId = bookerWorkerId ? bookerWorkerId : this.bookerWorkerId;
      // booker worker uuid ends

      // whitelabel starts
      const isWhiteLabeled: string = this.helperService.getQueryParamValues(base64Params, 'is-whitelabel', 'is_whitelabel');
      this.isWhiteLabeled = isWhiteLabeled ? isWhiteLabeled : this.isWhiteLabeled;
      // whitelabel ends

      // internal starts
      this.internal = base64Params['internal'] ? base64Params['internal'] : this.internal;
      // internal ends

      // show secret events starts
      const showSecretEvents: string = this.helperService.getQueryParamValues(base64Params, 'show-secret-events', 'show_secret_events');
      this.showSecretEvents = showSecretEvents ? showSecretEvents : this.showSecretEvents;
      // show secret events ends

      // event id starts
      const eventId: string = this.helperService.getQueryParamValues(base64Params, 'event-id', 'event_id');
      this.eventId = eventId ? eventId : this.eventId;
      // event id ends

      // store id starts
      const selectedStoreId: number = this.helperService.getQueryParamValues(base64Params, 'store_id', 'store-id');
      this.selectedStoreId = selectedStoreId ? selectedStoreId : this.selectedStoreId;
      // store id ends

      // store zip code starts
      const selectedStoreZip: number = this.helperService.getQueryParamValues(base64Params, 'store-zip', 'store_zip');
      this.selectedStoreZipCode = selectedStoreZip ? selectedStoreZip : this.selectedStoreZipCode;
      // store zip code ends

      // store name starts
      const selectedStoreName: string = this.helperService.getQueryParamValues(base64Params, 'store_name', 'store-name');
      this.selectedStoreName = selectedStoreName ? selectedStoreName : this.selectedStoreName;
      // store name ends

      // worker id starts
      let selectedWorkerId: number = this.helperService.getQueryParamValues(base64Params, 'worker_id', 'worker-id');
      selectedWorkerId = selectedWorkerId !== undefined && !isNaN(Number(selectedWorkerId)) ? Number(selectedWorkerId) : undefined;
      this.selectedWorkerId = selectedWorkerId !== undefined ? selectedWorkerId : this.selectedWorkerId;
      // worker id ends

      // worker ids (subset) starts — same []-first, plain-alias lookup as the URL parse
      const base64SelectedWorkerIds = this.helperService.getQueryParamValues(base64Params, 'worker_ids[]', 'worker-ids[]')
        ?? this.helperService.getQueryParamValues(base64Params, 'worker_ids', 'worker-ids');
      if (base64SelectedWorkerIds !== undefined) {
        this.selectedWorkerIds = base64SelectedWorkerIds;
      }
      // worker ids (subset) ends

      // worker email starts
      let selectedWorkerEmail: string = this.helperService.getQueryParamValues(base64Params, 'worker_email', 'worker-email');
      selectedWorkerEmail = selectedWorkerEmail ? selectedWorkerEmail : undefined;
      this.selectedWorkerEmail = selectedWorkerEmail ? selectedWorkerEmail.toLowerCase() : this.selectedWorkerEmail;
      // worker email ends

      // Appointment services start
      const appointmentServiceIds: any[] = this.helperService.getQueryStringKey('service', this.decodedBase64String, true);
      this.selectedAppointmentServiceIds = appointmentServiceIds ? appointmentServiceIds : this.selectedAppointmentServiceIds;
      // Appointment services ends

      // Filter stores start
      const filterStoresIds = this.helperService.getQueryParamValues(base64Params, 'filter-stores[]', 'filter_stores[]') as string;
      if (filterStoresIds) {
        this.filterStores = filterStoresIds.split(',');
      }
      // Filter stores ends

      // Filter categories start
      const filterCategoriesIds = this.helperService.getQueryParamValues(base64Params, 'filter-categories[]', 'filter_categories[]') as string;
      if (filterCategoriesIds) {
        this.filterCategories = filterCategoriesIds.split(',');
      }
      // Filter categories ends

      // Filter categories start
      const conferenceUuid = this.helperService.getQueryParamValues(base64Params, 'conference-uuid', 'conference_uuid') as string;
      if (conferenceUuid) {
        this.conferenceUuid = conferenceUuid.trim();
      }
      // Filter categories ends

      // internal comment
      const internalComment = this.helperService.getQueryParamValues(base64Params, 'internal_comment', 'internal-comment');
      this.internalComment = internalComment ? decodeURIComponent(internalComment) : this.internalComment;
      // internal comment

      // Appointment service categories start
      const appointmentServiceCategoryIds: any[] = this.helperService.getQueryStringKey('category', this.decodedBase64String, true);
      this.selectedAppointmentServiceCategoryIds = appointmentServiceCategoryIds && appointmentServiceCategoryIds[0]
        ? (<string>appointmentServiceCategoryIds[0]).split(',')
        : this.selectedAppointmentServiceCategoryIds;
      // Appointment service categories ends

      // Customer starts
      const tempCustomerData: CustomerPrefillDataModel = this.helperService.getQueryStringKey('customer', this.decodedBase64String, false);
      this.selectedJsonCustomerDetail = tempCustomerData ? tempCustomerData : this.selectedJsonCustomerDetail;
      if (this.selectedJsonCustomerDetail?.email) {
        this.selectedJsonCustomerDetail.email = this.helperService.fixEmailSpaceIssue(this.selectedJsonCustomerDetail.email);
      }
      // Customer ends

      // Broker starts
      const tempBrokerData: CustomerPrefillDataModel = this.helperService.getQueryStringKey('broker', this.decodedBase64String, false);
      this.selectedJsonBrokerDetail = tempBrokerData ? tempBrokerData : this.selectedJsonBrokerDetail;
      if (this.selectedJsonBrokerDetail?.email) {
        this.selectedJsonBrokerDetail.email = this.helperService.fixEmailSpaceIssue(this.selectedJsonBrokerDetail.email);
      }
      // Broker ends

      // Customer uuid starts
      const customerUuid: string = this.helperService.getQueryParamValues(base64Params, 'customer-uuid', 'customer_uuid');
      this.customerUuid = customerUuid ? customerUuid : this.customerUuid;
      // Customer uuid ends

      // Customer token starts
      const customerToken = this.helperService.getQueryParamValues(base64Params, 'customer-token', 'customer_token');
      this.customerToken = customerToken ? customerToken : this.customerToken;
      // Customer token ends

      // Customer profile starts
      const customerProfileUuid: string = this.helperService.getQueryParamValues(base64Params, 'customer-profile', 'customer_profile');
      this.customerProfileUuid = customerProfileUuid ? customerProfileUuid : this.customerProfileUuid;
      // Customer profile ends

      // Custom field starts
      const temp1: { [key: number]: { type: string, value: any } | any } = this.helperService
        .getQueryStringKey('custom_field', this.decodedBase64String, false);
      const temp2: { [key: number]: { type: string, value: any } | any } = this.helperService
        .getQueryStringKey('custom-field', this.decodedBase64String, false);
      if (temp1) {
        this.selectedJsonCustomFields = temp1;
      } else if (temp2) {
        this.selectedJsonCustomFields = temp2;
      }
      // Custom field ends
    }

    if (isNaN(Number(this.selectedStoreId))) {
      LoggerService.warn('[Debug] Invalid store id');
      this.selectedStoreId = undefined;
    }

    if (isNaN(Number(this.selectedStoreZipCode))) {
      LoggerService.warn('[Debug] Invalid store zip');
      this.selectedStoreZipCode = undefined;
    }

    if (this.selectedAppointmentServiceIds?.length > 0 && this.selectedAppointmentServiceIds !== 'NULL') {
      this.selectedAppointmentServiceIds = (this.selectedAppointmentServiceIds as string[]).filter((item: string) => {
        return Number(item);
      });
      if (this.selectedAppointmentServiceIds?.length > 0) {
        // Don't do anything as of now
      } else {
        this.selectedAppointmentServiceIds = undefined;
      }
    } else {
      this.selectedAppointmentServiceIds = undefined;
    }

    if (this.selectedAppointmentServiceCategoryIds?.length > 0 && this.selectedAppointmentServiceCategoryIds !== 'NULL') {
      this.selectedAppointmentServiceCategoryIds = (this.selectedAppointmentServiceCategoryIds as string[]).filter((item: string) => {
        return (Number(item));
      });
      if (this.selectedAppointmentServiceCategoryIds?.length > 0) {
        // Don't do anything as of now
      } else {
        this.selectedAppointmentServiceCategoryIds = undefined;
      }
    } else {
      this.selectedAppointmentServiceCategoryIds = undefined;
    }

    this.dateContext = this.helperService.isEmptyString(this.dateContext) ? undefined : this.dateContext;
    this.dateContextStart = this.helperService.isEmptyString(this.dateContextStart) ? undefined : this.dateContextStart;
    this.dateContextEnd = this.helperService.isEmptyString(this.dateContextEnd) ? undefined : this.dateContextEnd;
    this.calendarPreselectedDate = this.helperService.isEmptyString(this.calendarPreselectedDate) ? undefined : this.calendarPreselectedDate;

    // Listen date context enforcement event based event
    this.customEventService.updateWidgetDateContext
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(dateContext => {
        LoggerService.warn("[debug][date_context_enforcement] Apply date context based on selected date");
        this.dateContext = dateContext.date;
        this.dateContextStart = dateContext.date_context_start.toString();
        this.dateContextEnd = dateContext.date_context_end.toString();
      });

    // RR-v2: the channel is now resolved for BOTH embed modes — the `channel` attribute for
    // the booking-page web-component (isFrame=false skips the block above), the query
    // string for the iframe. Set the channel host unconditionally, after both blocks.
    this.partnerService.setBookingChannel(this.channel);

    LoggerService.log('[Attribute] channel ', this.channel);
    LoggerService.log('[Attribute] bookingName ', this.bookingName);
    LoggerService.log('[Attribute] bookingType ', this.bookingType);
    LoggerService.log('[Attribute] lang ', this._lang);
    LoggerService.log('[Attribute] workerUuid ', this.workerUuid);
    LoggerService.log('[Attribute] meeting_type_id ', this.meeting_type_id);
    LoggerService.log('[Attribute] customer_notification_preference ', this.customer_notification_preference);
    LoggerService.log('[Attribute] isWhiteLabeled ', this.isWhiteLabeled);
    LoggerService.log('[Attribute] internal ', this.internal);
    LoggerService.log('[Attribute] eventId ', this.eventId);
    LoggerService.log('[Attribute] selectedStoreId ', this.selectedStoreId);
    LoggerService.log('[Attribute] selectedStoreZipCode ', this.selectedStoreZipCode);
    LoggerService.log('[Attribute] selectedAppointmentServiceIds ', this.selectedAppointmentServiceIds);
    LoggerService.log('[Attribute] selectedWorkerId ', this.selectedWorkerId);
    LoggerService.log('[Attribute] selectedWorkerEmail ', this.selectedWorkerEmail);
    LoggerService.log('[Attribute] selectedJsonCustomFields ', this.selectedJsonCustomFields);
    LoggerService.log('[Attribute] selectedJsonCustomerDetail ', this.selectedJsonCustomerDetail);
    LoggerService.log('[Attribute] selectedJsonBrokerDetail ', this.selectedJsonBrokerDetail);
    LoggerService.log('[Attribute] bookerWorkerId ', this.bookerWorkerId);
    LoggerService.log('[Attribute] utmSource ', this.utmSource);
    LoggerService.log('[Attribute] utmId ', this.utmId);
    LoggerService.log('[Attribute] isLoggingEnabled ', this.isLoggingEnabled);
    LoggerService.log('[Attribute] isBookingDisabled ', this.isBookingDisabled);
    LoggerService.log('[Attribute] disableLocalStorage ', this.disableLocalStorage);
    LoggerService.log('[Attribute] template ', this.template);
    LoggerService.log('[Attribute] token ', this.token);
    LoggerService.log('[Attribute] internalComment', this.internalComment);
    LoggerService.log('[Attribute] date', this.calendarPreselectedDate);
    LoggerService.log('[Attribute] dateContext', this.dateContext);
    LoggerService.log('[Attribute] dateContextStart', this.dateContextStart);
    LoggerService.log('[Attribute] dateContextEnd', this.dateContextEnd);
    LoggerService.log('[Attribute] isPreview', this.isPreview);
    LoggerService.log('[Attribute] langSwitcher', this.langSwitcher);
    LoggerService.log('[Attribute] filterStores', this.filterStores);
    LoggerService.log('[Attribute] filterCategories', this.filterCategories);
    LoggerService.log('[Attribute] conferenceUuid', this.conferenceUuid);
    LoggerService.log('[Attribute] debug', this.debug);
    LoggerService.log('[Attribute] skip-qualification-question', this.skipQualificationQuestion);
    LoggerService.log('[Attribute] customerUuid ', this.customerUuid);
    LoggerService.log('[Attribute] coupon ', this.coupon);
    LoggerService.log('[Attribute] customerToken ', this.customerToken);
    LoggerService.log('[Attribute] customerProfileUuid ', this.customerProfileUuid);

    // DEV alert
    if (environment.baseUrl.includes('dev')) {
      this.showDevAlert = true;
    }

    // Show localstorage not accessible warning
    this.localStorageAccessible = this.helperService.storageAvailable('localStorage');

    // internal check to show customer selection in the personal-data-component
    if (this.internal === 'true' || this.debug === 'true') {
      this.internal === 'true' && (this.isInternal = true);

      if (!this.token || this.token === null || this.token === 'null') {
        this.setLanguage(this._lang, null);
        this.tokenError = true;
        this.tokenErrorMessage = 'common.verification_token_missing';
        return;
      } else {
        this.partnerService.partnerVerificationCode = this.token;
      }
    } else {
      this.debug = 'false';
      this.isInternal = false;
      this.debug = 'false';
      this.tokenError = false;
    }

    // detect if reference to calenso should be shown in footer
    this.whiteLabelBoolean = (this.isWhiteLabeled === 'true');

    if (Number(this.disableLocalStorage) === 1) {
      this.localStorageService.disableLocalStorage = true;
      this.localStorageService.clear(() => this.initWidgetConf());
    } else {
      this.initWidgetConf();
    }
  }

  initWidgetConf(): void {
    this.setWidgetConfData();
  }

  // Normalize a raw selected-worker-ids value ("5,7" string or number[]) into a deduped list of
  // positive ids. Any invalid element (NaN, 0 or negative) discards the WHOLE param — graceful
  // fallback, exactly like an invalid single worker_id. 0 is never allowed inside the array
  // (plain "anyone" stays worker_id=0).
  private normalizeWorkerIdsValue(raw: string | number[]): number[] | undefined {
    if (raw === undefined || raw === null || raw === '') {
      return undefined;
    }
    const rawList: (string | number)[] = Array.isArray(raw) ? raw : String(raw).split(',');
    const ids: number[] = [];
    for (const item of rawList) {
      const num = Number(item);
      if (isNaN(num) || num <= 0) {
        LoggerService.warn('[Attribute] Invalid selected-worker-ids, ignoring the whole parameter');
        return undefined;
      }
      ids.push(num);
    }
    return Array.from(new Set(ids));
  }

  /**
   * Whether a widget attribute was actually supplied.
   *
   * In webcomponent mode these values come straight from the host markup, where writing
   * `booker-worker-id=""` means "not set" — but `'' !== undefined` is true, so an empty attribute
   * used to read as an explicit worker and silently discard `selected-worker-ids`. `workerUuid`
   * was the only one of the four already testing for it.
   *
   * `0` counts as supplied: it is the "anyone" sentinel, not a missing value.
   */
  private hasAttributeValue(value: unknown): boolean {
    // Only the types these fields can legitimately hold: attributes and query parameters arrive as
    // strings, transformed ids as numbers. Anything else — undefined and null included — is not a
    // supplied value, and refusing to coerce it also means a host page cannot break widget init by
    // assigning an object whose toString() throws.
    if (typeof value !== 'string' && typeof value !== 'number') {
      return false;
    }
    // NaN must be rejected explicitly, and it is not hypothetical: an empty numeric attribute
    // arrives here as NaN, whose String() is the non-empty "NaN" — so an emptiness test alone
    // reads an unset attribute as supplied. (It is also why JSON.stringify is useless when
    // debugging this: JSON.stringify(NaN) prints null, which looks like a different bug.)
    if (typeof value === 'number' && Number.isNaN(value)) {
      return false;
    }
    return String(value).trim() !== '';
  }

  // Apply the worker-selection precedence once, after every config source has been parsed:
  // an explicit single identifier (worker_id/worker_email/worker-uuid/booker-worker-id) always
  // wins; a single valid id collapses into the scalar single-worker path; 2+ ids stay a subset.
  private resolveSelectedWorkerIds(): void {
    const normalized = this.normalizeWorkerIdsValue(this.selectedWorkerIds as string | number[]);
    const hasExplicitSingleWorker = this.hasAttributeValue(this.selectedWorkerId)
      || this.hasAttributeValue(this.selectedWorkerEmail)
      || this.hasAttributeValue(this.workerUuid)
      || this.hasAttributeValue(this.bookerWorkerId);
    if (!normalized || hasExplicitSingleWorker) {
      if (normalized && hasExplicitSingleWorker) {
        LoggerService.warn('[Attribute] selected-worker-ids ignored because an explicit single worker identifier is present');
      }
      this.selectedWorkerIds = undefined;
      return;
    }
    if (normalized.length === 1) {
      this.selectedWorkerId = normalized[0];
      this.selectedWorkerIds = undefined;
      return;
    }
    this.selectedWorkerIds = normalized;
  }

  setWidgetConfData() {
    if (!this.bookingName) {
      return;
    }

    this.resolveSelectedWorkerIds();

    const widgetConfBody: IPartnerInformationBody = {
      booking_name: this.bookingName,
      token_type: TokenType.publicWidget
    };

    // H1: in multi-worker subset mode send NEITHER worker_id NOR worker_email — the subset lives in
    // its own field, and any single identifier here would pin the store and hide the resource step.
    if (!this.selectedWorkerIds?.length) {
      if (this.selectedWorkerId !== undefined) {
        widgetConfBody.worker_id = this.selectedWorkerId;
      } else if (this.selectedWorkerEmail !== undefined) {
        widgetConfBody.worker_email = this.selectedWorkerEmail;
      }
    }

    // When widget is internal pass token and internal along with existing params
    // in partner information call so BE can generate new auth token
    if (this.internal === 'true' || this.debug === 'true') {
      widgetConfBody.token = this.token;

      if (this.internal === 'true') {
        widgetConfBody.internal = true;
        widgetConfBody.token_type = TokenType.internalWidget
      } else if (this.debug === 'true') {
        widgetConfBody.debug = true;
      }
    }

    return this.partnerService.getWidgetConf(widgetConfBody).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(partner => {
        const observables: {
          partner: Observable<PartnerDao>,
          customCss: Observable<CssOverwrites>,
          conference: Observable<ConferenceDbModel>,
          customer: Observable<CustomerDbModel>,
          widgetTemplates: Observable<CustomBookingMessageTemplate[]>
        } = {
          partner: of(null),
          customCss: of(null),
          conference: of(null),
          customer: of(null),
          widgetTemplates: of([])
        };

        this.helperService.loadIframeResizerJs(partner?.iframe_resizer_version);

        if (!partner) {
          return forkJoin(observables);
        }

        // Set partner
        observables.partner = of(partner);
        this.partnerService.partnerUUID = partner.uuid;


        // Drop the pool unless the partner is entitled (BE flattens plan/addon into this
        // flag) — mirrors the server gate that would otherwise 400.
        if (this.selectedWorkerIds?.length && Number(partner?.is_widget_multiple_workers_enabled) !== 1) {
          LoggerService.warn('[WARNING] Multiple workers is not enabled for this partner. Ignoring selected-worker-ids.');
          this.selectedWorkerIds = undefined;
        }

        // Get customer details if customer uuid is passed
        if (this.customerUuid) {
          observables.customer = this.customersService.getCustomersByUuid(this.customerUuid).pipe(
            takeUntilDestroyed(this.destroyRef),
            catchError(() => { return of(null); }),
            switchMap(response => {
              this.customer = response?.customer || null;
              return of(this.customer);
            })
          );
        } else {
          observables.customer = of(null);
        }

        // Read here, before the localStorage wipe in modify*LocalStorageData: the record lives in
        // sessionStorage and is only resumed when it matches the return on the URL.
        this.takeAdyenReturn(partner?.id);

        if (this.redirectedPaymentIntent && this.redirectedPaymentIntentClientSecret) {
          LoggerService.log('[Redirected Payment Intent] Redirected from Stripe payment verification');
        } else if (this.adyenResume || this.adyenReturnedElsewhere) {
          // Parked by this tab, which had passed the protection before it paid - or a tab that only
          // says where the booking is finished, and books nothing.
          LoggerService.log('[Adyen] Returning to a payment this tab parked');
        } else {
          if (partner?.is_widget_password_protection_setting_enabled === 1) {
            if (partner?.widget_protection_type === WIDGET_PROTECTION_TYPE.OTP) {
              if (partner?.otp_restricted_components?.includes(OTP_RESTRICTED_COMPONENTS.APPOINTMENT_BOOKING)) {
                this.isWidgetPasswordProtected = true;
              } else {
                this.isWidgetPasswordProtected = false;
              }
            } else {
              this.isWidgetPasswordProtected = true;
            }
          }
        }

        // If partner has the custom CSS feature, fetch overwrites
        if (this.isPreview || Number(partner?.is_custom_css_widget_enabled) === 1) {
          observables.customCss = this.template
            ? this.partnerService.getCssOverwriteUsingUuid(this.template, this.isPreview)
            : this.partnerService.getCssOverwrites();
        }

        // When conference uuid is passed grab conference details
        if (this.conferenceUuid !== '' && this.conferenceUuid !== undefined) {
          observables.conference = this.conferenceService.getConferenceDetails(this.conferenceUuid).pipe(
            switchMap(conference => {
              this.appointmentService.appointment_services_uuids = conference.appointment_services?.map(conf => conf.uuid);
              if (conference?.partner_id !== partner.id) {
                this.errorInConference = 'common.conference_not_belonging';
                LoggerService.warn('[Conference] The provided conference UUID belongs to a different partner.');
                return of(null);
              }

              if (conference?.uuid !== undefined && conference?.is_expired === false && ((!conference?.is_permanent && conference?.is_booked === false) || conference?.is_permanent)) {
                this.conferenceState.uuid = conference.uuid;
                this.appointmentService.appointment_services_uuids = conference.appointment_services?.map(conf => conf.uuid);

                // Set worker id
                if (conference?.worker_id) {
                  this.conferenceState.worker_id = conference.worker_id;
                }

                // Set store id
                if (conference?.worker) {
                  this.selectedStoreId = conference.worker.store_id;
                  this.conferenceState.store_id = conference.worker.store_id;
                }

                // Set appointment service ids
                if (conference?.appointment_services) {
                  conference.appointment_service_ids = conference.appointment_services?.map(service => service.id);
                  const mappedServiceIdsObj: MappedAppointmentServicesDao = {
                    0: {
                      serviceIds: conference.appointment_service_ids,
                      is_multiple_select: false
                    }
                  };
                  this.appointmentService.mappedServiceIds = mappedServiceIdsObj;
                  this.conferenceState.appointment_service_ids = conference.appointment_service_ids;
                }

                // Set worker ids
                if (conference.booking_links_workers?.length) {
                  this.conferenceState.worker_ids = [];
                  this.conferenceState.worker_uuids = [];
                  conference.booking_links_workers.forEach(worker => {
                    this.conferenceState.worker_ids.push(Number(worker.worker_id));
                    this.conferenceState.worker_uuids.push(worker.worker.uuid);
                  });
                  this.workerUuids = this.conferenceState.worker_uuids;
                }

                // set date context if present
                if (conference.date_context_from && conference.date_context_to) {
                  const dateContextStart = moment(conference.date_context_from);
                  const dateContextEnd = Math.abs(dateContextStart.diff(moment(conference.date_context_to), 'days'));

                  this.dateContext = dateContextStart.format('YYYY-MM-DD');
                  this.dateContextStart = '0';
                  this.dateContextEnd = dateContextEnd.toString();
                }
              } else {
                if (!conference?.uuid) {
                  this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.INVALID);
                } else if (conference?.is_booked) {
                  this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.BOOKED);
                } else if (conference?.is_expired) {
                  this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.EXPIRED);
                } else {
                  this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.INVALID);
                }

                return of(null);
              }

              return of(conference);
            }),
            catchError((error: HttpErrorResponse) => {
              LoggerService.error(error);
              if (error.status === CONFERENCE_ERROR_CODES.BOOKED) {
                this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.BOOKED);
              } else if (error.status === CONFERENCE_ERROR_CODES.NOT_BELONGING) {
                this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.NOT_BELONGING);
              } else if (error.status === CONFERENCE_ERROR_CODES.EXPIRED) {
                this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.EXPIRED);
              } else if (error.status === CONFERENCE_ERROR_CODES.INVALID) {
                this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.INVALID);
              } else {
                this.errorInConference = this.conferenceErrorHandler(CONFERENCE_ERROR_STATE.INVALID);
              }

              return of(null);
            }),
          );
        }

        observables.widgetTemplates = this.partnerService.getWidgetTemplates().pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => { return of([]); }),
          switchMap(templates => { return of(templates) })
        );

        return forkJoin(observables);
      })
    ).subscribe({
      next: partnerConfigs => {
        this.getTimezones();

        // Set widget templates
        this.widgetTemplates = partnerConfigs.widgetTemplates;

        // The partner's own wording for the dead end where nothing on this link is bookable.
        //
        // `templates/widget` returns every template it lists, with the partner's override merged
        // into `_translations` when there is one - so a template is ALWAYS found and its presence
        // says nothing. The view therefore tests the RESOLVED TEXT, not this object.
        this.resolveErrorTemplates(partnerConfigs.widgetTemplates);
        // Set AFTER the templates resolve: these flags only gate template-backed alerts, so
        // flipping them in the earlier stage made the alert render its standard text first and
        // swap to the partner's once the fetch landed - a visible flash on every such error.
        this.applyBookingTypeAvailability(partnerConfigs.partner);

        if (
          (this.redirectedPaymentIntent && this.redirectedPaymentIntentClientSecret)
          || this.adyenResume
          || this.adyenReturnedElsewhere
        ) {
          LoggerService.log('[Redirected Payment] Returning from a payment verification');
        } else {
          // Check if widget restrictions are enabled and parameters are configured
          // OR if not, check if customer authentication is enabled
          if (
            partnerConfigs.partner?.enable_widget_restriction === 1 &&
            partnerConfigs.partner?.widget_restriction_parameters?.trim() !== ''
          ) {
            this.validateWidgetRestrictions(partnerConfigs.partner);
          } else if (partnerConfigs.partner?.is_customer_authentication_enabled === 1) {
            this.isCustomerAuthenticationEnabled = true;
            this.authenticateCustomerToken();
          }
        }

        this.enableLangSetter = true;
        this.setLanguage(this._lang, (partnerConfigs.partner?.id ? partnerConfigs.partner : null));

        // Handle token verification errors
        if (partnerConfigs.partner?.errors) {
          this.tokenError = true;
          if (partnerConfigs.partner?.errors[0].code === HttpStatusCode.Forbidden) {
            this.tokenErrorMessage = 'common.invalid_verification_code'
          } else if (partnerConfigs.partner?.errors[0].code === HttpStatusCode.Unauthorized) {
            this.tokenErrorMessage = 'common.verification_token_missing'
          } else {
            this.showSubscriptionError = true;
          }

          return;
        } else {
          this.tokenError = false;
        }

        if (!partnerConfigs.partner?.subscription) {
          this.showSubscriptionError = true;
          return;
        }

        if (!partnerConfigs.partner?.has_bookable_workers) {
          this.showWorkersNotAvailableError = true;
        }

        // Handle custom css
        this.setCssProperties(partnerConfigs.partner, partnerConfigs.customCss);

        if (this.selectedWorkerId || this.selectedWorkerEmail) {
          if (partnerConfigs.partner.worker) {
            this.foundedWorker = partnerConfigs.partner.worker;
            this.selectedWorkerId = this.foundedWorker.id;
            LoggerService.log('[Debug] Setting hide_resource_step to 1 because worker was preselected.');
            partnerConfigs.partner.hide_resource_step = 1;
            if (!this.selectedStoreId && !this.selectedStoreName) {
              this.selectedStoreId = this.foundedWorker.store_id;
            } else if (this.selectedStoreId) {
              // Keep a validly-served secondary store (multi-store addon). Only reset to
              // the primary when the worker does not serve the selected store at all.
              if (!workerServesStore(this.foundedWorker, Number(this.selectedStoreId))) {
                LoggerService.warn('[Debug] Store Id and Worker store id is not matching. Resetting store id to worker store id.');
                this.selectedStoreId = this.foundedWorker.store_id;
              } else {
                // Keep the pre-PR invariant that this step always yields a number — the raw URL
                // param is a string, and downstream strict comparisons silently miss on it.
                this.selectedStoreId = Number(this.selectedStoreId);
              }
            }
            LoggerService.log('[Debug] Selected Store ID after foundedWorker', this.selectedStoreId);
          } else {
            if (Number(this.selectedWorkerId) === 0) {
              LoggerService.warn('[Debug] Selected worker ID is 0. Setting any worker and setting hide_resource_step to 1.');
              this.selectedWorkerId = 0;
              partnerConfigs.partner.hide_resource_step = 1;
            } else {
              LoggerService.warn('[Debug] Selected worker ID cannot be found in the list of workers. Ignoring the selected worker ID and setting hide_resource_step to 0.');
              this.selectedWorkerId = undefined;
              partnerConfigs.partner.hide_resource_step = 0;
            }
          }
        }

        this.googleAnalyticsService.partnerAnalyticsObj = partnerConfigs.partner.analytics;
        this.googleAnalyticsService.partnerSubscriptionPlan = partnerConfigs.partner.subscription;
        this.analytics = partnerConfigs.partner.analytics;
        if (partnerConfigs.partner?.adobe_launch_tracking_url && partnerConfigs.partner?.adobe_launch_tracking_url?.trim() !== '') {
          this.loadAdobeAnalytics(partnerConfigs.partner.adobe_launch_tracking_url);
        }
        this.widgetBookingInfo = partnerConfigs.partner.widget_booking_info;

        // Manually setting partner configs to do not allow book multiple appointments
        // while booking conference
        if (this.conferenceState.uuid !== undefined) {
          partnerConfigs.partner.is_cart_supported = 0;
          partnerConfigs.partner.hide_widget_cart_functionality_message = 0;
        }

        this.widgetConf = WidgetConf.createWidgetConfObject(partnerConfigs.partner);

        this.widgetUtilService.setWidgetConf(this.widgetConf);
        this.dateUtilsService.setWidgetTimeFormat(partnerConfigs.partner.widget_time_format);
        this.customizeAppearance();

        this.bookTypesEnabled = this.widgetUtilService.getBookingTypes(this.bookingType, this.widgetConf);
        // A returning Adyen payment opens the type it was paid on - the first type may be another one.
        this.currentBookingType = this.adyenResumeBookingType() ?? this.bookTypesEnabled[0];

        if (this.currentBookingType === this.widgetConstants.APPOINTMENT) {
          this.modifyAppointmentLocalStorageData(partnerConfigs.partner.booking_name, partnerConfigs.partner);
        } else if (this.currentBookingType === this.widgetConstants.EVENT) {
          this.modifyEventLocalStorageData(partnerConfigs.partner.booking_name, partnerConfigs.partner);
        } else if (this.currentBookingType === this.widgetConstants.PRE_DEFINED_SLOT) {
          this.modifyAppointmentLocalStorageData(partnerConfigs.partner.booking_name, partnerConfigs.partner);
        } else if (this.currentBookingType === this.widgetConstants.TOUR) {
          this.modifyAppointmentLocalStorageData(partnerConfigs.partner.booking_name, partnerConfigs.partner);
        } else {
          this.modifyAppointmentLocalStorageData(partnerConfigs.partner.booking_name, partnerConfigs.partner);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.enableLangSetter = true;
        // Read BEFORE setLanguage: with no partner it resolves to the brand default and writes
        // that back over `_lang`, losing what the embed asked for.
        this.requestedLang = this._lang;
        this.setLanguage(this._lang, null);

        // A blocked/deleted partner (partner_blocked) or a partner whose booking
        // widget is switched off (booking_widget_disabled, JB-1637) makes
        // partners/information return 403, so no partner data ever arrives.
        // Without this the widget sits on the skeleton loader forever, which
        // reads as "still loading" rather than "not available".
        const errorCode: string | undefined = error.status === HttpStatusCode.Forbidden
          ? error.error?.errors?.[0]?.error_code
          : undefined;
        if (errorCode === WIDGET_ERROR_CODES.PARTNER_BLOCKED) {
          this.isPartnerBlocked = true;
        } else if (errorCode === WIDGET_ERROR_CODES.BOOKING_WIDGET_DISABLED) {
          // JB-1637: the booking widget is switched off. The success-path setter at
          // ~L1000 never runs on a 403, so flip the flag here to render the existing
          // "widget disabled — contact the provider" message.
          this.bookingWidgetMasterSettingsEnabled = false;
          // The partner's own wording travels WITH the refusal. It cannot be fetched here:
          // templates/widget derives the partner from the JWT, and a 403 means no token was
          // ever issued, so that call answers 401 however it is retried. Keyed by locale,
          // because the server cannot know which language this widget is in.
          this.widgetDisabledWordings = this.toWordingMap(error.error?.errors?.[0]?.partner_wording);
        } else {
          // Unknown 403 code, or any non-403 failure (5xx / network) — a likely
          // transient problem, so show "something went wrong, try again later"
          // rather than a stuck skeleton or the misleading "contact the provider"
          // copy meant for a deliberately-off widget.
          this.hasBootstrapError = true;
        }

        LoggerService.error(error);
      }
    });
  }

  /**
   * The template indexes this map by a URL-supplied language, so it has no prototype:
   * `?language=constructor` must find nothing rather than Object's own members.
   */
  private toWordingMap(wording: Record<string, unknown> | undefined): Record<string, string> {
    const map: Record<string, string> = Object.create(null);
    for (const [lang, text] of Object.entries(wording ?? {})) {
      if (typeof text === 'string') {
        map[lang] = text;
      }
    }
    return map;
  }

  /**
   * Flags the booking types this partner does not offer. Template-only - nothing in
   * TypeScript reads these, they purely decide which dead-end alert the view renders.
   */
  private applyBookingTypeAvailability(partner: PartnerDao): void {
    if (Number(partner?.bookingwidget_enabled) === 0) {
      this.bookingWidgetMasterSettingsEnabled = false;
      LoggerService.warn('[WARNING] The booking widget is temporary disabled. Please contact the Calenso support');
    }
    if (!partner?.widget_type_preferences?.includes(WIDGET_APPOINTMENT_TYPE.EXTERNAL_APPOINTMENT)) {
      this.appointmentExternalBookingEnabled = false;
      LoggerService.warn('[WARNING] Appointment booking is not enabled.');
    }
    if (!partner?.widget_type_preferences?.includes(WIDGET_APPOINTMENT_TYPE.INTERNAL_APPOINTMENT)) {
      this.appointmentInternalBookingEnabled = false;
      LoggerService.warn('[WARNING] Internal Appointment booking is not enabled.');
    }
    if (!partner?.widget_type_preferences?.includes(WIDGET_APPOINTMENT_TYPE.EXTERNAL_GROUP_APPOINTMENT)) {
      this.eventExternalBookingEnabled = false;
      LoggerService.warn('[WARNING] Event booking is not enabled.');
    }
    if (!partner?.widget_type_preferences?.includes(WIDGET_APPOINTMENT_TYPE.EXTERNAL_PRE_DEFINED_SLOTS)) {
      this.pdsExternalBookingEnabled = false;
      LoggerService.warn('[WARNING] Pre-defined slots booking is not enabled.');
    }
    if (!partner?.widget_type_preferences?.includes(WIDGET_APPOINTMENT_TYPE.EXTERNAL_TOUR)
        || Number(partner?.is_dynamic_combi_enabled) !== 1) {
      this.tourExternalBookingEnabled = false;
      LoggerService.warn('[WARNING] Tour booking is not enabled.');
    }
  }

  /**
   * Resolves the partner-configurable dead-end messages.
   *
   * `templates/widget` returns every template it lists, with the partner's override merged
   * into `_translations` when there is one - so a template is ALWAYS found and its presence
   * says nothing. The views therefore test the RESOLVED TEXT, not these objects.
   */
  private resolveErrorTemplates(templates: CustomBookingMessageTemplate[]): void {
    this.widgetTemplates = templates ?? [];

    const labels = WIDGET_CONSTANTS.WIDGET_LABELS;
    this.nothingBookableTemplate = this.helperService.findTemplate(this.widgetTemplates, labels.WIDGET_ERROR_NOTHING_BOOKABLE);
    this.widgetDisabledTemplate = this.helperService.findTemplate(this.widgetTemplates, labels.WIDGET_ERROR_WIDGET_DISABLED);
    this.configurationErrorTemplate = this.helperService.findTemplate(this.widgetTemplates, labels.WIDGET_ERROR_CONFIGURATION);
    this.authenticationFailedTemplate = this.helperService.findTemplate(this.widgetTemplates, labels.WIDGET_ERROR_AUTHENTICATION_FAILED);
    this.subscriptionErrorTemplate = this.helperService.findTemplate(this.widgetTemplates, labels.WIDGET_ERROR_SUBSCRIPTION);
    this.tourUnavailableTemplate = this.helperService.findTemplate(this.widgetTemplates, labels.WIDGET_ERROR_TOUR_UNAVAILABLE);
  }

  setCssProperties(partner: PartnerDao, customCss: CssOverwrites): void {
    if (this.template) {
      if (customCss === null) {
        LoggerService.warn('[css-overwrite] Invalid css-overwrite', this.template);
      } else if (!this.isPreview && partner?.id !== customCss?.partner_id) {
        LoggerService.warn('[css-overwrite] Partner id is not matching for apply pre-define css-overwrite');
      } else if (customCss?.type !== 'widget') {
        LoggerService.error('[css-overwrite] Invalid css-overwrite type', customCss?.type);
      } else {
        LoggerService.log('[css-overwrite] Applying css-overwrite provided in url parameter', customCss?.uuid);
        this.applyCustomCss(customCss);
      }
    } else {
      if (customCss?.is_active) {
        LoggerService.log('[css-overwrite] Applying activated css-overwrite manually', customCss?.uuid);
        this.applyCustomCss(customCss);
      }
    }
  }

  /**
   * `@fortawesome/fontawesome-svg-core` inserts its stylesheet (`.svg-inline--fa { height: 1em … }`)
   * into `document.head` the first time an icon renders — invisible inside a shadow tree, which
   * leaves every `<fa-icon>` SVG unsized. Same CSS, placed inside the shadow root, together with the
   * intl-tel-input asset overrides.
   */
  private injectRuntimeCssIntoShadowRoot(): void {
    const shadowRoot = this.hostElementRef.nativeElement.shadowRoot;
    if (!shadowRoot) {
      return;
    }
    const style = document.createElement('style');
    style.setAttribute('data-runtime-css', 'shadow-root');
    style.textContent = [
      fontAwesomeDom.css(),
      this.intlTelInputAssetOverrides(),
    ].join('\n');
    shadowRoot.prepend(style);
  }

  /**
   * The iframe embed's automatic resizing is driven by the iframe-resizer CHILD script (loaded in
   * `HelperService.loadIframeResizerJs()`), and the two versions differ in a way that matters here:
   *
   * - v4.3.5 — the DEFAULT, since `iframe_resizer_version` is `'5.5.0'` for only a few partners —
   *   detects content changes with a `MutationObserver` on `document.body`. That observer cannot see
   *   mutations inside a shadow tree, so once the widget renders into a shadow root nothing
   *   re-triggers a resize and the iframe keeps its first-paint height: content gets clipped or
   *   trapped in an inner scrollbar as the booking advances. This trigger is what fixes that.
   * - v5.5 observes `document.body` with a `ResizeObserver`, which DOES see the shadow host grow, so
   *   v5 partners were most likely never affected. The trigger is harmless there, but v5 renamed
   *   `size()` to `resize()` and warns on each deprecated call WITHOUT memoising it — so calling
   *   `size()` would print a console warning on the partner's page on every height change.
   *
   * The host element grows with its shadow content either way, so watching the host and asking the
   * child SDK to re-measure is the right trigger. Do NOT remove it: v4 partners need it.
   */
  private observeHostSizeForIframeResizer(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const host = this.hostElementRef.nativeElement;
    let lastHeight = 0;
    let frame: number = null;

    const observer = new ResizeObserver(() => {
      // The resize we ask for changes the box we observe, so the callback is rAF-throttled and only
      // acts on a real height change — an unguarded one loops ("ResizeObserver loop completed with
      // undelivered notifications").
      if (frame !== null) {
        return;
      }
      frame = requestAnimationFrame(() => {
        frame = null;
        const height = Math.ceil(host.getBoundingClientRect().height);
        if (height === lastHeight) {
          return;
        }
        lastHeight = height;
        // The child API only appears once the script has handshaked with the parent frame; outside an
        // iframe — the web component embed — it never exists and this is a no-op. The SDK measures
        // for itself, we only ask it to. Prefer v5's `resize()`: its `size()` alias is deprecated and
        // logs an un-memoised warning on the partner's page every single time it is called.
        const child = (window as any).parentIframe ?? (window as any).parentIFrame;
        if (typeof child?.resize === 'function') {
          child.resize();
        } else {
          child?.size?.();
        }
      });
    });

    observer.observe(host);

    this.destroyRef.onDestroy(() => {
      frame !== null && cancelAnimationFrame(frame);
      observer.disconnect();
    });
  }

  /**
   * intl-tel-input's CSS references its flag sprite as `url("../img/flags.webp")` — relative to
   * the stylesheet, which the build rewrites to a relative path inside the bundle. In the light
   * DOM that stylesheet is the linked `styles.css`, so the browser resolves it against the widget
   * origin; inside a shadow `<style>` it is resolved against the host page instead (Chromium keeps
   * `url()` in custom properties relative until they are used). The sprite is therefore shipped
   * unhashed under `assets/intl-tel-input/` (angular.json) and addressed absolutely here.
   */
  private intlTelInputAssetOverrides(): string {
    const base = `${environment.deployUrl}/assets/intl-tel-input`;
    return `:host {
      --iti-path-flags-1x: url("${base}/flags.webp");
      --iti-path-flags-2x: url("${base}/flags@2x.webp");
      --iti-path-globe-1x: url("${base}/globe.webp");
      --iti-path-globe-2x: url("${base}/globe@2x.webp");
    }`;
  }

  applyCustomCss(css: CssOverwrites): void {
    this.cssOverwrites = css.code;
    // A <style> in the document (as before): needed for the surfaces rendered OUTSIDE the shadow
    // root (CDK overlays: datepicker, dialogs) and for `@font-face` / `@import` declarations, which
    // only load from the document.
    const style = document.createElement('style');
    style.setAttribute('scoped', '');
    style.innerHTML = this.cssOverwrites;
    document.body.append(style);

    const host = this.hostElementRef.nativeElement;
    const shadowRoot = host.shadowRoot;
    if (shadowRoot) {
      // The partner CSS must ALSO live inside the shadow tree to reach the widget. Selectors anchored
      // above the shadow tree (`:root`, `html`, `body`, the custom element itself, a wrapper of the
      // host page) never match inside it, so they are re-targeted to the shadow host with their
      // specificity preserved — see `rewriteCustomCssForShadowRoot()`.
      const hostAncestorMatches = (compound: string) => host.closest(compound) !== null;
      let shadowCss = this.cssOverwrites;
      try {
        shadowCss = rewriteCustomCssForShadowRoot(this.cssOverwrites, environment.bookingElementName, hostAncestorMatches);
      } catch (error) {
        // never let a malformed stylesheet take the rest of the partner bootstrap down: the browser's
        // own parser is far more forgiving, so adopt the text as-is (only document-anchored rules are lost)
        LoggerService.warn('[css-overwrite] Could not rewrite the partner CSS for the shadow root, adopting it as-is', error);
      }
      this.applyCustomCssToShadowRoot(shadowRoot, shadowCss);
    }
  }

  /**
   * A constructed stylesheet cascades AFTER every `<style>` of the shadow tree — including the child
   * component styles Angular keeps appending while components render — so the partner CSS keeps the
   * "always last" position it has in light DOM (a `<body>` style after all `<head>` styles).
   */
  private applyCustomCssToShadowRoot(shadowRoot: ShadowRoot, cssText: string): void {
    if ('adoptedStyleSheets' in shadowRoot && typeof CSSStyleSheet !== 'undefined' && 'replaceSync' in CSSStyleSheet.prototype) {
      try {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(cssText);
        shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
        return;
      } catch (error) {
        LoggerService.warn('[css-overwrite] Constructed stylesheet rejected, falling back to a <style> element', error);
      }
    }
    const style = document.createElement('style');
    style.setAttribute('scoped', '');
    style.innerHTML = cssText;
    shadowRoot.append(style);
  }

  private conferenceErrorHandler(state: string): string {
    if (state === CONFERENCE_ERROR_STATE.BOOKED) {
      return 'common.conference_booked';
    } else if (state === CONFERENCE_ERROR_STATE.NOT_BELONGING) {
      return 'common.conference_not_belonging';
    } else if (state === CONFERENCE_ERROR_STATE.EXPIRED) {
      return 'common.conference_expired_link';
    } else if (state === CONFERENCE_ERROR_STATE.INVALID) {
      return 'common.conference_invalid_link';
    } else {
      return 'common.conference_invalid_link';
    }
  }

  switchForm(bType: string): void {
    this.currentBookingType = bType;
  }

  /** The returning Adyen payment, for the host that was paying - every other host gets none. */
  protected adyenResumeFor(host: AdyenResumeHost): AdyenResumeReturn | undefined {
    return this.adyenResume?.record.snapshot.host === host ? this.adyenResume : undefined;
  }

  /**
   * Matches the Adyen return captured on load with the payment this tab parked for the partner.
   *
   * A hosted return with no record came back into a tab that did not start the payment (a new tab
   * on a phone, a lost opener): the tab that did books it by polling. This one only says so - a
   * booking flow here would let the shopper pay a second time for the same booking.
   */
  private takeAdyenReturn(partnerId: number | undefined): void {
    this.adyenResume = this.adyenResumeService.takeReturn(partnerId, this.adyenReturnParams) ?? undefined;
    this.adyenReturnedElsewhere = !this.adyenResume && !!this.adyenReturnParams?.hostedNonce;
    this.adyenReturnParams = null;
  }

  /**
   * The way on from the "returned elsewhere" notice when the tab that started the payment is gone.
   * The return was already removed from the URL on load, so the page loads as a fresh booking.
   */
  protected startNewBooking(): void {
    window.location.reload();
  }

  /**
   * The booking type that renders the host a returning Adyen payment was made on, or null when there
   * is no such payment. A host this widget cannot show any more cannot finish it: the record is
   * dropped (the reconciliation job reverses the payment) and the shopper is told so.
   */
  private adyenResumeBookingType(): string | null {
    if (!this.adyenResume) {
      return null;
    }
    const type = adyenResumeBookingType(this.adyenResume.record.snapshot.host, this.bookTypesEnabled, this.isCompactMode);
    if (type === null) {
      LoggerService.warn('[Adyen] The booking type the payment was made on is not offered here - not resumed');
      this.adyenResumeService.clear(this.adyenResume.record.partnerId);
      this.adyenResume = undefined;
      this.adyenResumeNoticeKey = ADYEN_RESUME_CONSTANTS.MESSAGE_KEYS.CONTEXT_CHANGED;
    }
    return type;
  }

  /**
   * Customizes brackground color of the widget based on partner settings
   */
  customizeAppearance(): void {
    if (this.widgetUtilService.getWidgetConf()?.backgroundColor && this.calioSectionRef) {
      this.calioSectionRef.nativeElement.style.backgroundColor = this.widgetUtilService.getWidgetConf()?.backgroundColor;
    }
  }

  modifyEventLocalStorageData(bookingName: string, partner: PartnerDao): void {
    this.localStorageService.getItem(EVENT_CONSTANT.EVENT_GLOBAL_USER_LS + bookingName, (savedUserState: UserState) => {
      this.localStorageService.getItem(EVENT_CONSTANT.EVENT_CUSTOM_FIELDS_LS + bookingName, (savedCustomFieldsState) => {
        this.localStorageService.getItem(EVENT_CONSTANT.EVENT_STRIPE_REDIRECTION_DATA_LS, (savedStripeRedirectionDataState) => {
          this.localStorageService.clear(() => {
            if (savedUserState) {
              this.utilService.updateLocalStorage(EVENT_CONSTANT.EVENT_GLOBAL_USER_LS + bookingName, savedUserState);
            }
            if (savedCustomFieldsState) {
              this.utilService.updateLocalStorage(EVENT_CONSTANT.EVENT_CUSTOM_FIELDS_LS + bookingName, savedCustomFieldsState);
            }
            if (savedStripeRedirectionDataState) {
              this.eventStripeRedirectionData = savedStripeRedirectionDataState as Record<string, any>;
            }
            this.setUpWidgetData(partner);
          });
        });
      });
    });
  }

  modifyAppointmentLocalStorageData(bookingName: string, partner: PartnerDao): void {
    this.localStorageService.getItem(APPOINTMENT_CONSTANT.APPOINTMENT_GLOBAL_USER_LS + bookingName, (savedUserState: UserState) => {
      this.localStorageService.getItem(APPOINTMENT_CONSTANT.APPOINTMENT_CUSTOMFIELDS_LS_NAME + bookingName, (savedCustomFieldsState) => {
        this.localStorageService.getItem(APPOINTMENT_CONSTANT.APPOINTMENT_STRIPE_REDIRECTION_DATA_LS_NAME, (savedAppointmentStripeRedirectionDataState) => {
          this.localStorageService.getItem(APPOINTMENT_CONSTANT.PDS_STRIPE_REDIRECTION_DATA_LS_NAME, (savedPdsStripeRedirectionDataState) => {
            this.localStorageService.clear(() => {
              if (savedUserState) {
                this.utilService.updateLocalStorage(APPOINTMENT_CONSTANT.APPOINTMENT_GLOBAL_USER_LS + bookingName, savedUserState);
              }
              if (savedCustomFieldsState) {
                this.utilService.updateLocalStorage(APPOINTMENT_CONSTANT.APPOINTMENT_CUSTOMFIELDS_LS_NAME + bookingName, savedCustomFieldsState);
              }
              if (savedAppointmentStripeRedirectionDataState) {
                this.appointmentStripeRedirectionData = savedAppointmentStripeRedirectionDataState as Record<string, any>;
              }
              if (savedPdsStripeRedirectionDataState) {
                this.pdsStripeRedirectionData = savedPdsStripeRedirectionDataState as Record<string, any>;
              }
              this.setUpWidgetData(partner);
            });
          });
        });
      });
    });
  }

  setUpWidgetData(partner: PartnerDao) {
    this.globals = this.widgetUtilService.createGlobalObjectData(
      this.widgetConf,
      this.eventId,
      this.isInternal,
      this.whiteLabelBoolean,
      this._lang,
      (this.debug === 'true'),
      (this.debug === 'true') ? this.token : undefined
    );

    this.globals.showSecretEvents = this.showSecretEvents === 'true' || this.isInternal;

    this.translateService.use(this._lang);

    // Set partner data object to be passed to all child components
    this.partnerGlobalData = this.widgetUtilService.createPartnerGlobalData(this.widgetConf, this._lang, partner);
    this.widgetColorConf = this.widgetUtilService.createWidgetColorConf(partner);

    if (this.globals.isInternal && !this.tokenError && this.appointmentInternalBookingEnabled) {
      this.customersService.getCustomers()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: customers => this.globals.customers = customers,
          error: (error: HttpErrorResponse) => LoggerService.error(error)
        });
    }
  }

  onBookingSuccessfull(): void {
    // NOTE: deliberately NOT clearing enteredAppointmentFromTour / prefetchedTourCombiService here —
    // we need to know on "Book another appointment" click that the user originated from tour, so we can
    // route them back to the tour factory page instead of the regular services step.
    this.calioSectionRef && this.calioSectionRef.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  onTourCombiPrepared(response: DynamicCombiPrepareResponse): void {
    // Feed the full combi service DAO from /prepare into the existing combi-service selection flow —
    // the appointment component will inject it into the services list and treat it like a static combi click.
    this.selectedAppointmentServiceIds = [String(response.combi_service_id)];
    this.hideStoreSelection = true;
    this.enteredAppointmentFromTour = true;
    this.prefetchedTourCombiService = response.combi_service ?? null;
    // Capture the user's tour selection so back-nav from DATE step (or book-again-from-tour) can
    // restore it when the tour factory page re-mounts.
    this.tourLastSelectedUuids = (response.services ?? []).map(s => s.uuid);
    this.currentBookingType = this.widgetConstants.APPOINTMENT;
    LoggerService.log('[tour] combi prepared, switching to appointment flow', response);
  }

  onBackToTour(payload?: { resetSelection: boolean }): void {
    this.selectedAppointmentServiceIds = [];
    this.hideStoreSelection = false;
    this.enteredAppointmentFromTour = false;
    this.prefetchedTourCombiService = null;
    // resetSelection=true after a successful booking ("book another appointment") — start the tour
    // factory fresh. Default (false / undefined) preserves the previous selection so back-from-DATE
    // restores the user's picks.
    if (payload?.resetSelection) {
      this.tourLastSelectedUuids = [];
    }
    this.currentBookingType = this.widgetConstants.TOUR;
    LoggerService.log('[tour] back from appointment flow, restoring tour view', payload);
  }

  onMaintenancePageButton(): void {
    window.open(environment.statusUrl, '_blank');
  }

  loadAdobeAnalytics(adobe_launch_tracking_url: string) {
    try {
      const script = document.createElement('script');
      script.src = adobe_launch_tracking_url;
      script.async = true;
      document.head.appendChild(script);
    } catch (ex) {
      LoggerService.warn('Error appending google analytics');
      LoggerService.error(ex);
    }
  }

  widgetPasswordVerificationSuccess(): void {
    this.isWidgetPasswordProtected = false;
  }

  private validateWidgetRestrictions(partner: PartnerDao): void {
    const requiredParams = partner.widget_restriction_parameters.split(',').map(p => p.trim());
    const paramValues = {
      [WIDGET_RESTRICTION_PARAMETERS.CUSTOMER_UUID]: this.customerUuid && this.customer?.uuid,
      [WIDGET_RESTRICTION_PARAMETERS.WORKER_ID]: this.selectedWorkerId,
      [WIDGET_RESTRICTION_PARAMETERS.WORKER_EMAIL]: this.selectedWorkerEmail,
      [WIDGET_RESTRICTION_PARAMETERS.STORE_ID]: this.selectedStoreId,
      [WIDGET_RESTRICTION_PARAMETERS.SERVICE_ID]: this.selectedAppointmentServiceIds,
      [WIDGET_RESTRICTION_PARAMETERS.CUSTOMER_TOKEN]: this.customerToken
    };

    for (const param of requiredParams) {
      if (!this.hasValidParameter(param, paramValues)) {
        LoggerService.warn(`[Widget Restriction] ${param} is missing`);
        this.isWidgetRestrictionValidationFailed = true;

        return;
      } else if (partner?.is_customer_authentication_enabled === 1) {
        // If customer authentication is enabled, set the customer authentication in progress and hold the flow
        // until the customer authentication is completed
        this.isCustomerAuthenticationEnabled = true;
        this.authenticateCustomerToken();

        return;
      }
    }

    this.isWidgetRestrictionValidationFailed = false;
  }

  private authenticateCustomerToken(): void {
    this.isCustomerAuthenticationInProgress = true;
    if (!this.customerToken) {
      this.isCustomerAuthenticationInProgress = false;
      this.isCustomerAuthenticationFailed = true;
      return;
    }

    this.customersService.authenticateCustomerToken({ token: this.customerToken })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isCustomerAuthenticationInProgress = false;
          this.isCustomerAuthenticationEnabled = false;
        },
        error: () => {
          this.isCustomerAuthenticationInProgress = false;
          this.isCustomerAuthenticationFailed = true;
        }
      });
  }

  private hasValidParameter(param: string, paramValues: { [key: string]: any }): boolean {
    const value = paramValues[param];
    return value !== undefined && value !== null && value !== '';
  }

  private getTimezones(): void {
    this.timezoneService.getTimezones()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: timezones => {
          this.timezones = timezones?.data ?? [];
          const localTimezone = this.timezoneService.getLocalTimezone();
          const timezone = this.timezones.find(timezone => timezone.zone_group.includes(localTimezone))?.zone;
          if (timezone) {
            this.timezoneService.localTimezone = timezone;
          }
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error),
      });
  }
}
